import {ExportToCsv} from 'export-to-csv';

import {
  formatAggregatedDate,
  formattedFeatureName,
} from 'app/components/Dashboard/utils/chartUtils';
import {numberWithCommas} from 'app/utils/conversionUtils';

import {FeatureStatsRowData} from '../MultiFeatureAnalysisWidget/MultiFeatureAnalysisTable';
import {BarChartCategoryData, FeatureStatsApiResponse, MultifeatureAnalysisConfig} from '../types';

// Turns a feature stats api response into table rows
export function formatMultiFeatureTableRows(
  featureStats: FeatureStatsApiResponse
): FeatureStatsRowData[] {
  return Object.entries(featureStats)
    .map(([featureId, featureStat]) => {
      // If there is an error, don't include the row
      // in the table.
      if ('error' in featureStat) {
        return null;
      }
      return {
        ...featureStat.stats,
        ...featureStat.properties,
        id: featureId,
        name: formattedFeatureName(featureStat.properties),
      };
    })
    .filter((row) => row !== null);
}

// Turns categorical area chart data into table rows.
// Categorical chart api response needs to be grouped before
// it can be turned into table rows. We do this only once
// with the getCategoricalData function, and pass that
// result here to avoid repeating potentially expensive
// computations.
export function formatCategoricalAreaTableRows(
  data: BarChartCategoryData[],
  allDates: string[]
): FeatureStatsRowData[] {
  const rows: FeatureStatsRowData[] = [];
  for (const featureData of data) {
    const row = {
      id: featureData.id,
      name: featureData.label ?? 'Unknown',
      ...featureData.properties,
    } as FeatureStatsRowData;
    if (featureData.backgroundColor) {
      row.color = featureData.backgroundColor;
    }
    for (let i = 0; i < allDates.length; i++) {
      row[allDates[i]] = featureData.data[i];
    }
    rows.push(row);
  }
  return rows;
}

export function getSummaryText(
  dataViewUnit: 'area' | 'percentage',
  areaUnit: 'acres' | 'hectares',
  currentCategory: string
) {
  let string = 'Table displays ';

  if (dataViewUnit === 'percentage' && currentCategory !== 'multiCategory') {
    string += `each property's share of total "${currentCategory}" area`;
    return string;
  }

  if (dataViewUnit === 'area' && currentCategory === 'multiCategory') {
    string += `the area in ${areaUnit} of each category`;
    return string;
  }

  if (dataViewUnit === 'percentage' && currentCategory === 'multiCategory') {
    string += `percentage share of the total area by category`;
    return string;
  }

  if (dataViewUnit === 'area' && currentCategory !== 'multiCategory') {
    string += `the "${currentCategory}" area in ${areaUnit} for each property`;
    return string;
  }

  return string;
}
export const formatTableValue = (value: number | null | undefined): string => {
  return typeof value === 'number' ? numberWithCommas(value.toFixed(value >= 1000 ? 0 : 2)) : '-';
};

/**
 * Handles downloading chart data as CSV.
 * Uses the table rows and widget config to generate a CSV file.
 *
 *
 */
export const handleDownloadCsv = (
  tableRows: FeatureStatsRowData[],
  widgetConfig: MultifeatureAnalysisConfig,
  onError?: () => void
) => {
  try {
    if (!tableRows || tableRows.length === 0) {
      if (onError) onError();
      return;
    }

    // For each row, remove keys that aren't the name or a date
    const filteredTableRows = tableRows.map((row) => {
      return Object.fromEntries(
        Object.entries(row)
          .filter(([key]) => key === 'name' || !isNaN(new Date(key).getTime()))
          .map(([key, value]) => [
            key,
            typeof value === 'string'
              ? value
              : formatTableValue(value as number | null | undefined),
          ])
      );
    });

    // Use the keys as the table headers
    const headers = Object.keys(filteredTableRows[0]).map((key) => {
      if (key === 'name') return 'Property Name';
      return formatAggregatedDate(key, widgetConfig.timeAggregation, widgetConfig.layerKey);
    });

    // Generate and download CSV
    const fileName = `${widgetConfig.title || 'chart-data'}-${new Date().toISOString().split('T')[0]}`;
    new ExportToCsv({
      showLabels: true,
      useBom: false,
      filename: fileName,
      headers,
    }).generateCsv(filteredTableRows);
  } catch (err) {
    console.error('Error generating CSV:', err);
    if (onError) onError();
  }
};
